import { useKeyboardOverlap } from '@shared-ui/platform/hooks/use-keyboard-overlap'
import { type ComponentRef, forwardRef, type PropsWithChildren } from 'react'
import { ScrollView, type ScrollViewProps, View, type ViewProps, type ViewStyle } from 'react-native'

type KeyboardOffset = {
  closed?: number
  opened?: number
}

export type KeyboardStickyViewProps = ViewProps & {
  offset?: KeyboardOffset
  enabled?: boolean
}

export function KeyboardProvider({ children }: PropsWithChildren) {
  return children
}

export function KeyboardStickyView({ offset, enabled = true, style, ...props }: KeyboardStickyViewProps) {
  const keyboardInset = useKeyboardOverlap()
  const isOpen = enabled && keyboardInset > 0
  const spacing = isOpen ? (offset?.opened ?? 0) : (offset?.closed ?? 0)
  const marginBottom = (isOpen ? keyboardInset : 0) + spacing

  return <View style={[style, marginBottom > 0 && { marginBottom }]} {...props} />
}

export type KeyboardAwareScrollViewProps = ScrollViewProps & {
  bottomOffset?: number
  enabled?: boolean
  extraKeyboardSpace?: number
}

export const KeyboardAwareScrollView = forwardRef<ComponentRef<typeof ScrollView>, KeyboardAwareScrollViewProps>(
  function KeyboardAwareScrollViewComponent(props, ref) {
    const {
      contentContainerStyle,
      bottomOffset: _bottomOffset,
      extraKeyboardSpace = 0,
      enabled = true,
      style,
      children,
      ...rest
    } = props
    const keyboardInset = useKeyboardOverlap()
    const extraSpace = enabled && keyboardInset > 0 ? keyboardInset + extraKeyboardSpace : 0

    return (
      <ScrollView
        ref={ref}
        {...rest}
        style={style}
        contentContainerStyle={[contentContainerStyle, extraSpace > 0 && { paddingBottom: extraSpace }]}
      >
        {children}
      </ScrollView>
    )
  },
)

export type KeyboardAvoidingViewProps = ViewProps & {
  behavior?: 'padding' | 'height' | 'position'
  enabled?: boolean
  keyboardVerticalOffset?: number
}

export function KeyboardAvoidingView({
  behavior = 'padding',
  enabled = true,
  keyboardVerticalOffset = 0,
  style,
  children,
  ...props
}: KeyboardAvoidingViewProps) {
  const keyboardInset = useKeyboardOverlap()
  const inset = enabled ? Math.max(0, keyboardInset + keyboardVerticalOffset) : 0

  const avoidingStyle: ViewStyle | undefined =
    behavior === 'padding' && inset > 0 ? { paddingBottom: inset } : undefined

  return (
    <View style={[style, avoidingStyle]} {...props}>
      {children}
    </View>
  )
}
